import {
  Container,
  Copy,
  Flex,
  Heading,
  Section,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react/dist';
import { debounce } from '@grubhubprod/umami-library/dist/nodash/debounce';
import { subscribe } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import classnames from 'classnames';
import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { format as dateFormat } from 'date-fns';
import { useParams } from 'react-router-dom';
import DateRangeSelection from 'src/components/DateRange/DateRangeSelection';
import { Input } from 'src/components/Input/Input';
import { useDateRange } from 'src/components/selfService/features/OrderHistory/hooks/useDateRange';
import { getDefaultDateRange } from 'src/components/selfService/features/OrderHistory/OrderHistory.utils';
import OrderHistoryTable from 'src/components/selfService/features/OrderHistory/OrderHistoryTable';
import { DownloadZipController } from 'src/components/selfService/features/OrderHistory/ZipDownload/DownloadZipController';
import { Select } from 'src/components/Select/Select';
import { SortFields, SortOrder } from 'src/components/Table/SortableColumn';
import { IOrdersHistory } from 'src/types/OrderHistory';
import { ISearchResponse } from 'src/types/SearchResponse';
import { sdk } from 'src/utilities/sdk';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_SEARCH_TIMING = 200;
const DATE_FORMAT = 'MMM d';
export interface ISortOption {
  label: string;
  value: { field: SortFields; order: SortOrder };
}

const SORT_BY_OPTION = 'Sort by';
export const SortOptionsList: ISortOption[] = [
  {
    label: 'Most recent',
    value: { field: SortFields.WHEN_FOR, order: SortOrder.DESC },
  },
  {
    label: 'Oldest',
    value: { field: SortFields.WHEN_FOR, order: SortOrder.ASC },
  },
  {
    label: 'Name: A-Z',
    value: { field: SortFields.DINER_NAME, order: SortOrder.ASC },
  },
  {
    label: 'Name: Z-A',
    value: { field: SortFields.DINER_NAME, order: SortOrder.DESC },
  },
  {
    label: 'Restaurant: A-Z',
    value: { field: SortFields.RESTAURANT, order: SortOrder.ASC },
  },
  {
    label: 'Restaurant: Z-A',
    value: { field: SortFields.RESTAURANT, order: SortOrder.DESC },
  },
];

const searchDefaultState: ISearchResponse<IOrdersHistory> = {
  total_hits: 0,
  total_pages: 0,
  results: [],
  page: 1,
  query: '',
};

export interface ISearchQueryParams {
  query?: string;
  size: number;
  page: number;
  sort_field: SortFields;
  sort_order: SortOrder;
}

export interface ISearchFilters {
  start?: string;
  end?: string;
}

export default function OrderHistory(): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const selectSortRef = useRef(null);
  const searchInputRef = useRef(null);

  const { isPhone, isDesktop } = useScreenSizes();
  const [searchResult, setSearchResult] =
    useState<ISearchResponse<IOrdersHistory>>(searchDefaultState);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
  const [showError, setShowError] = useState(false);
  const [orders, setOrders] = useState<IOrdersHistory[]>([]);
  const [previousSortField, setPreviousSortField] = useState(
    SortFields.WHEN_FOR
  );
  const [previousSortOrder, setPreviousSortOrder] = useState(SortOrder.DESC);
  const [noResultsForCorp, setNoResultsForCorp] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);

  const [currentSortSelectValue, setCurrentSortSelectValue] =
    useState(SORT_BY_OPTION);
  const [currentSearchInputValue, setCurrentSearchInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { startDate, endDate, setDateRange } = useDateRange({
    corpId: corpid as string,
  });

  const initSearchDone = useRef(false);

  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    // send analytics
    sendTealiumPageView({
      pageGroup: 'corporate self service - account management',
      pageName: 'Order History',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function search({
    query,
    page = 1,
    isLoadingMore,
    sortField,
    sortOrder,
    filters,
  }: {
    query: string;
    page: number;
    isLoadingMore: boolean;
    sortField: SortFields;
    sortOrder: SortOrder;
    filters: ISearchFilters;
  }) {
    let params: ISearchQueryParams = {
      size: DEFAULT_PAGE_SIZE,
      page,
      sort_field: sortField,
      sort_order: sortOrder,
    };

    // empty query value produces 0 results
    if (query) {
      params = { ...params, query };
    }

    try {
      if (isLoadingMore) {
        setIsLoadingMoreResults(true);
      } else {
        setIsLoading(true);
      }

      setCurrentPage(page);
      const response: ISearchResponse<IOrdersHistory> = await sdk.http.post(
        `/corps/${corpid}/orders`,
        filters,
        { searchParams: params }
      );

      setShowError(false);
      setSearchResult(response);
      setNoResultsForCorp(false);

      if (isLoadingMore) {
        const moreResults = [...orders, ...response.results];
        setOrders(moreResults);
        setIsLoadingMoreResults(false);
      } else {
        setOrders(response.results);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoadingMoreResults(false);
      setIsLoading(false);
      setShowError(true);
    }
  }

  async function initialSearch() {
    try {
      const response: ISearchResponse<IOrdersHistory> = await sdk.http.post(
        `/corps/${corpid}/orders`,
        getSearchFilters(),
        {
          searchParams: {
            size: DEFAULT_PAGE_SIZE,
            page: 1,
            sort_field: SortFields.WHEN_FOR,
            sort_order: SortOrder.DESC,
          },
        }
      );
      initSearchDone.current = true;

      if (response.results.length === 0) {
        setNoResultsForCorp(true);
      } else {
        setNoResultsForCorp(false);
        setSearchResult(response);
        setOrders(response.results);
      }
    } catch (error) {
      setShowError(true);
    }
  }

  useEffect(() => {
    const subscription = subscribe('navBar:hidden', (data) => {
      setShowToolbar(!data.isNavBarVisible);
      selectSortRef.current.value = currentSortSelectValue;
      searchInputRef.current.value = currentSearchInputValue;
    });

    return () => {
      subscription();
    };
  }, [currentSortSelectValue, currentSearchInputValue]);

  useEffect(() => {
    if (corpid && !initSearchDone.current) {
      initialSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  useEffect(() => {
    if (corpid && initSearchDone.current) {
      // set default date range when changing corpId for users with multiple corps
      const { startDate, endDate } = getDefaultDateRange();
      search({
        query: '',
        page: 1,
        isLoadingMore: false,
        sortField: SortFields.WHEN_FOR,
        sortOrder: SortOrder.DESC,
        filters: {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
        },
      });
      setDateRange({
        startDate,
        endDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  async function loadMore(isRetry: boolean) {
    await search({
      query: currentSearchInputValue,
      page: isRetry ? currentPage : currentPage + 1,
      isLoadingMore: true,
      sortField: previousSortField,
      sortOrder: previousSortOrder,
      filters: getSearchFilters(),
    });
  }

  async function onSort(sortField: SortFields, sortOrder: SortOrder) {
    setPreviousSortField(sortField);
    setPreviousSortOrder(sortOrder);
    await search({
      query: currentSearchInputValue,
      page: 1,
      isLoadingMore: false,
      sortField,
      sortOrder,
      filters: getSearchFilters(),
    });
  }

  function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    const value = (event.target as HTMLSelectElement).value;
    const option = SortOptionsList.find((o) => value === o.label);
    if (value !== SORT_BY_OPTION) {
      onSort(option.value.field, option.value.order);
      setCurrentSortSelectValue(value);
    }
  }

  function handleOnInput(event: FormEvent<HTMLInputElement>) {
    const value = (event.target as HTMLInputElement).value;
    setCurrentSearchInputValue(value);
    const debouncedSearch = debounce(() => {
      search({
        query: value,
        page: 1,
        isLoadingMore: false,
        sortField: previousSortField,
        sortOrder: previousSortOrder,
        filters: getSearchFilters(),
      });
    }, DEBOUNCE_SEARCH_TIMING);
    debouncedSearch();
  }

  async function onDateRangeApplied(
    currentStartDate: Date,
    currentEndDate: Date
  ) {
    setDateRange({
      startDate: currentStartDate,
      endDate: currentEndDate,
    });
    await search({
      query: currentSearchInputValue,
      page: 1,
      isLoadingMore: false,
      sortField: previousSortField,
      sortOrder: previousSortOrder,
      filters: {
        start: currentStartDate ? currentStartDate.toISOString() : null,
        end: currentEndDate ? currentEndDate.toISOString() : null,
      },
    });
  }

  function getSearchFilters() {
    return {
      start: startDate ? startDate.toISOString() : null,
      end: endDate ? endDate.toISOString() : null,
    };
  }

  function getDateRangeText(): string {
    return startDate && endDate
      ? `${dateFormat(new Date(startDate), DATE_FORMAT)} - ${dateFormat(
          new Date(endDate),
          DATE_FORMAT
        )}`
      : 'Date range';
  }

  function getSearchBar(): JSX.Element {
    return (
      <Flex
        justifyContent="space-between"
        flexDirection={isPhone ? 'column' : 'row'}
      >
        <Stack
          y={isPhone ? 'md' : 'none'}
          x={isDesktop ? 'sm' : 'x-sm'}
          width={isPhone ? '100%' : '50%'}
        >
          <Input
            key="searchTerm"
            ariaLabel="Search by name or restaurant"
            placeholder="Search by name or restaurant"
            name="searchTerm"
            id="searchTerm"
            ref={searchInputRef}
            onInput={handleOnInput}
            prefixIcon={
              <Copy variant="body" color="secondaryText">
                <SvgMagnifyingGlass size="16px" />
              </Copy>
            }
          />
        </Stack>
        <Stack width="100%">
          <Flex
            flexDirection={!isPhone ? 'row' : 'column'}
            justifyContent="space-between"
          >
            <Flex justifyContent="space-between">
              <Stack
                x="x-sm"
                width={isPhone ? '50%' : '160px'}
                minWidth="141px"
              >
                <DateRangeSelection
                  label={getDateRangeText()}
                  startDateValue={startDate}
                  endDateValue={endDate}
                  onApply={onDateRangeApplied}
                />
              </Stack>

              {!isDesktop && (
                <Stack
                  x={!isPhone ? 'x-sm' : 'none'}
                  y={isPhone ? 'md' : 'none'}
                  width={isPhone ? '50%' : 'auto'}
                >
                  <Select
                    name="sort"
                    id="sort"
                    onChange={handleChange}
                    ref={selectSortRef}
                  >
                    <option value={null}>{SORT_BY_OPTION}</option>
                    {SortOptionsList.map((option: ISortOption) => (
                      <option key={option.label} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Stack>
              )}
            </Flex>
            <Flex
              alignSelf={!isPhone ? 'center' : 'flex-end'}
              flexDirection="column"
              width={isPhone ? '100%' : 'auto'}
              minWidth="135px"
            >
              <Stack y={showToolbar ? 'none' : 'x-sm'}>
                <DownloadZipController
                  dateFrom={startDate}
                  dateTo={endDate}
                  query={currentSearchInputValue}
                  corpId={corpid as string}
                  isDisabled={!startDate || !endDate || orders.length === 0}
                  totalReceipts={searchResult.total_hits}
                />
              </Stack>
              {!(isPhone && showToolbar) && (
                <Flex justifyContent="flex-end" alignItems="center">
                  <Copy variant="body" color="secondaryText">
                    {`${orders.length} of ${searchResult.total_hits}`}
                  </Copy>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Stack>
      </Flex>
    );
  }

  const toolBar = getSearchBar();

  return (
    <>
      {showToolbar && (
        <Container variant="fluid">
          <Section
            inset="md"
            bg="untintedBackground"
            dimension="2"
            className={classnames('', {
              navBarFixed: showToolbar,
            })}
          >
            {toolBar}
          </Section>
        </Container>
      )}

      <Container variant="default">
        <Section inset="md">
          <Stack y={isDesktop ? 'md' : 'sm'}>
            <Heading variant="h1">Order activity</Heading>
          </Stack>
          {!showToolbar && <Stack y="x-sm">{toolBar}</Stack>}

          <OrderHistoryTable
            orderList={orders}
            pageSize={DEFAULT_PAGE_SIZE}
            totalItems={searchResult.total_hits}
            showLoadMore={orders.length < searchResult.total_hits}
            showError={showError}
            onLoadMore={loadMore}
            isLoadingMoreResults={isLoadingMoreResults}
            isLoading={isLoading}
            previousSortOrder={previousSortOrder}
            previousSortField={previousSortField}
            onSortColumnClick={onSort}
            initialSearchCompletedWithNoResults={noResultsForCorp}
          />
        </Section>
      </Container>
    </>
  );
}
